import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import {
  ConnectionState,
  type IncomingMessage,
  MessageAction,
} from '@/components/@Shared/Chat/Chat.types'

import { useMessageServiceMessageControllerGetAllMessagesKey } from '@/__generated__/queries'
import type { MessageData } from '@/__generated__/requests'

const INITIAL_RECONNECT_DELAY_MS = 1000
const MAX_RECONNECT_DELAY_MS = 30000

export function useUpdateMessages() {
  const queryClient = useQueryClient()

  const updateMessages = useCallback(
    (newMessage: MessageData) => {
      queryClient.setQueryData(
        [useMessageServiceMessageControllerGetAllMessagesKey],
        (data: any) => {
          if (data && data.pages) {
            const messageExists = data.pages
              .flat()
              .some((msg: MessageData) => msg.id === newMessage.id)

            if (messageExists) {
              return data
            }

            return {
              ...data,
              pages: [[newMessage, ...data.pages[0]], ...data.pages.slice(1)],
            }
          }

          return {
            pageParams: [],
            pages: [[newMessage]],
          }
        },
      )
    },
    [queryClient],
  )

  return updateMessages
}

type UseChatSocketParams = {
  onMessage: (message: IncomingMessage) => void
  recipientId: number
}

type UseChatSocketResult = {
  connectionState: ConnectionState
  sendMessage: (content: string) => boolean
}

export function useChatSocket({
  onMessage,
  recipientId,
}: UseChatSocketParams): UseChatSocketResult {
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    ConnectionState.CONNECTING,
  )

  const socketRef = useRef<WebSocket | null>(null)
  const onMessageRef = useRef(onMessage)

  useEffect(() => {
    onMessageRef.current = onMessage
  }, [onMessage])

  const sendMessage = useCallback((content: string) => {
    const socket = socketRef.current

    if (!socket || socket.readyState !== WebSocket.OPEN) {
      return false
    }

    socket.send(
      JSON.stringify({
        action: MessageAction.SEND_MESSAGE,
        content,
      }),
    )

    return true
  }, [])

  useEffect(() => {
    let disposed = false
    let reconnectTimeout: number | null = null
    let attempts = 0

    const connect = () => {
      if (disposed) return

      setConnectionState(
        attempts === 0
          ? ConnectionState.CONNECTING
          : ConnectionState.RECONNECTING,
      )

      const socket = new WebSocket(
        `${import.meta.env.VITE_WEBSOCKET_URL}?recipientId=${recipientId}`,
      )

      socket.onopen = () => {
        if (disposed) return

        attempts = 0
        setConnectionState(ConnectionState.OPEN)
      }

      socket.onmessage = event => {
        if (disposed) return

        const message = JSON.parse(event.data)

        if (message.action === MessageAction.RECEIVE_MESSAGE) {
          onMessageRef.current({
            content: message.content,
            id: message.id,
          })
        }
      }

      socket.onerror = () => {
        socket.close()
      }

      socket.onclose = () => {
        if (disposed) return

        const delay = Math.min(
          INITIAL_RECONNECT_DELAY_MS * 2 ** attempts,
          MAX_RECONNECT_DELAY_MS,
        )

        attempts += 1
        setConnectionState(ConnectionState.RECONNECTING)

        reconnectTimeout = window.setTimeout(connect, delay)
      }

      socketRef.current = socket
    }

    connect()

    return () => {
      disposed = true

      if (reconnectTimeout !== null) {
        window.clearTimeout(reconnectTimeout)
        reconnectTimeout = null
      }

      socketRef.current?.close()
      socketRef.current = null
    }
  }, [recipientId])

  return { connectionState, sendMessage }
}
